import { createAction, props } from '@ngrx/store';
import { PaceBarcode } from '../barcode-clipboard-collection.state';

/**
 * Barcode clipboard store actions.
 *
 * `PaceBarcode` is already a plain, serializable shape, so payloads carry it directly - no model
 * conversion needed. `correlationId` on the write/outcome actions lets the facade match a persisted
 * result back to the specific `setBarcodeClipboardRequested`/`uploadBarcodeClipboardRequested` it
 * dispatched, so its returned observable settles for that write rather than for a concurrent one.
 */

// --- Lifecycle ---------------------------------------------------------------

/** (re)hydrate the clipboard from storage. */
export const hydrateBarcodeClipboard = createAction('[BarcodeClipboard] Fetch requested');

/**
 * Dispatched by the hydrate effect once the persisted clipboard has been read (or migrated from the
 * older concurrent-usage entry, or defaulted to empty). Routed to the same handler as
 * {@link setBarcodeClipboardRequested} - a hydrate result is just as authoritative a replacement
 * of the clipboard as an explicit set.
 */
export const barcodeClipboardLoadedFromStorage = createAction(
    '[BarcodeClipboard] Loaded from storage',
    props<{ barcodes: PaceBarcode[] }>(),
);

// --- Bulk setter ---------------------------------------------------------------

/**
 * Dispatched by the facade's `setBarcodeClipboard()` to replace the clipboard wholesale. The
 * reducer applies the retention cap; the persist effect then mirrors the capped collection to
 * storage under the same `correlationId`.
 */
export const setBarcodeClipboardRequested = createAction(
    '[BarcodeClipboard] Set requested',
    props<{ barcodes: PaceBarcode[]; correlationId: string }>(),
);

// --- Outcome ---------------------------------------------------------------

/**
 * Settle-only signal that the clipboard was persisted successfully (mapped from the generic
 * `slicePersisted`). The store is already the source of truth, so this carries no clipboard
 * payload - it only lets the facade's write bridge complete for the matching `correlationId`.
 */
export const barcodeClipboardPersisted = createAction(
    '[BarcodeClipboard] Persisted',
    props<{ correlationId?: string }>(),
);

/** Dispatched when a persist/hydrate write for the clipboard fails (mapped from `persistenceError`). */
export const barcodeClipboardStorageError = createAction(
    '[BarcodeClipboard] Storage error',
    props<{ error: string; correlationId?: string }>(),
);
